import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { FilterCenterFocus, TrendingUp } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';
import apiService from '../../../../lib/services/apiService';
import { 
  buildSankeyNodes, 
  buildSankeyLinks,
  formatNumber,
  formatConversionRate,
  calculateFunnelEfficiency
} from '../../../../lib/utils/conversionFunnelUtils';

// Import supporting components
import FunnelStageCard from './FunnelStageCard';
import SankeyDiagramComponent from './SankeyDiagramComponent';
import DemographicConversionComparison from './DemographicConversionComparison';
import DropoffAnalysisPanel from './DropoffAnalysisPanel';

/**
 * ConversionFunnelAnalysis Component
 * 
 * Main component integrating:
 * - 5-stage funnel overview
 * - Demographic cohort analysis
 * - Sankey diagram visualization
 * - Conversion rate comparison
 * - Drop-off analysis
 * 
 * Uses the new /api/v1/analytics/conversion-funnel-analysis endpoint
 */
const ConversionFunnelAnalysis = ({ imdbId, onApiUnavailable }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  
  // User selections
  const [selectedDemographic, setSelectedDemographic] = useState('agegroup');
  const [minSampleSize, setMinSampleSize] = useState(30);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'demographic', 'dropoff'

  // Fetch funnel analysis data
  useEffect(() => {
    if (!imdbId) return;

    const fetchFunnelData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Fetching conversion funnel analysis...', {
          imdbId,
          demographics: ['agegroup', 'gender', 'ethnicity', 'movie_frequency_category'],
          minSampleSize
        });

        // Fetch funnel analysis with all demographics
        const response = await apiService.getConversionFunnelAnalysis(imdbId, {
          demographics: ['agegroup', 'gender', 'ethnicity', 'movie_frequency_category'],
          minSampleSize,
          filmRecencyOrder: 1 // Most recent round
        });

        console.log('✅ Funnel analysis loaded:', response);

        setFunnelData(response.data);
      } catch (err) {
        console.error('❌ Failed to load funnel analysis:', err);
        const errorMessage = err.message || 'Failed to load funnel analysis';
        setError(errorMessage);
        
        // If API not found/not ready and callback provided, trigger fallback
        if ((errorMessage.includes('not found') || errorMessage.includes('404') || errorMessage.includes('400') || errorMessage.includes('Invalid request')) && onApiUnavailable) {
          setTimeout(() => onApiUnavailable(), 100);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, [imdbId, minSampleSize, onApiUnavailable]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    // Check if it's a 404/400 (API not deployed yet or data not ready)
    const isApiNotAvailable = error.includes('not found') || error.includes('404') || error.includes('400') || error.includes('Invalid request');
    
    return (
      <Paper sx={{ p: 4, mb: 3 }}>
        <Alert severity={isApiNotAvailable ? 'info' : 'error'} sx={{ mb: 3 }}>
          {isApiNotAvailable ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Enhanced Conversion Funnel API Not Yet Available
              </Typography>
              <Typography variant="body2">
                The new conversion funnel analysis endpoint (<code>/api/v1/analytics/conversion-funnel-analysis</code>) 
                is not ready yet. This may be because the API hasn't been fully deployed or the data for this film hasn't been processed yet. 
                This feature will be available once the backend is updated.
              </Typography>
            </Box>
          ) : (
            error
          )}
        </Alert>
        
        {isApiNotAvailable && (
          <Box sx={{ mt: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              What's Coming in the Enhanced Funnel:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✨ <strong>5-Stage Accurate Tracking</strong> - Aware → Interested → Willing to Pay → Theater → Opening Weekend
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✨ <strong>Demographic Cohort Analysis</strong> - See WHO converts at each stage
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✨ <strong>Sankey Diagrams</strong> - Visual flow analysis by demographic segment
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✨ <strong>Drop-off Analysis</strong> - Identify where audiences are being lost
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✨ <strong>API-Generated Insights</strong> - Best/worst performers automatically identified
              </Typography>
              <Typography component="li" variant="body2">
                ✨ <strong>3 Interactive View Modes</strong> - Overview, Demographic Flow, and Drop-off Analysis
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Contact your system administrator to deploy the backend API update.
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }

  if (!funnelData || !funnelData.funnel_overview) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No funnel data available for this film.
      </Alert>
    );
  }

  const { 
    film, 
    funnel_overview, 
    demographic_breakdowns, 
    insights,
    visualization_hints 
  } = funnelData;

  // Get selected demographic breakdown
  const selectedDemo = demographic_breakdowns?.find(d => d.demographic === selectedDemographic);

  // Build Sankey data
  const sankeyNodes = selectedDemo ? buildSankeyNodes(selectedDemo, visualization_hints?.color_schemes) : [];
  const sankeyLinks = selectedDemo ? buildSankeyLinks(selectedDemo, sankeyNodes) : [];

  // Calculate overall funnel efficiency
  const efficiency = calculateFunnelEfficiency(funnel_overview);

  // Available demographics
  const availableDemographics = demographic_breakdowns?.map(d => ({
    value: d.demographic,
    label: d.demographic.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  })) || [];

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${brandColors.primary}10, ${brandColors.secondary}10)` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterCenterFocus sx={{ color: brandColors.primary, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.primary }}>
              Conversion Funnel Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {film?.title || 'Unknown Film'} • {formatNumber(film?.total_responses || 0)} Total Responses
            </Typography>
          </Box>
        </Box>

        {/* Overall Funnel Efficiency */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<TrendingUp />}
            label={`Overall Funnel Efficiency: ${formatConversionRate(efficiency)}`}
            sx={{
              backgroundColor: efficiency >= 30 ? `${brandColors.success}20` : efficiency >= 15 ? `${brandColors.warning}20` : `${brandColors.error}20`,
              color: efficiency >= 30 ? brandColors.success : efficiency >= 15 ? brandColors.warning : brandColors.error,
              fontWeight: 600,
              fontSize: '0.875rem',
              py: 2
            }}
          />
          <Typography variant="caption" color="text.secondary">
            (Aware → Opening Weekend conversion)
          </Typography>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Demographic to Analyze</InputLabel>
              <Select
                value={selectedDemographic}
                label="Demographic to Analyze"
                onChange={(e) => setSelectedDemographic(e.target.value)}
              >
                {availableDemographics.map(demo => (
                  <MenuItem key={demo.value} value={demo.value}>
                    {demo.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                label="View Mode"
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="overview">Overview</MenuItem>
                <MenuItem value="demographic">Demographic Flow</MenuItem>
                <MenuItem value="dropoff">Drop-off Analysis</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Min Sample Size</InputLabel>
              <Select
                value={minSampleSize}
                label="Min Sample Size"
                onChange={(e) => setMinSampleSize(e.target.value)}
              >
                <MenuItem value={10}>10+ responses</MenuItem>
                <MenuItem value={30}>30+ responses (recommended)</MenuItem>
                <MenuItem value={50}>50+ responses</MenuItem>
                <MenuItem value={100}>100+ responses</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Overview Mode */}
      {viewMode === 'overview' && (
        <Box>
          {/* 5-Stage Funnel Cards */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            5-Stage Funnel Overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={1}
                stageData={funnel_overview.stage_1_aware}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={2}
                stageData={funnel_overview.stage_2_interested}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={3}
                stageData={funnel_overview.stage_3_willing_to_pay}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={4}
                stageData={funnel_overview.stage_4_theater}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={5}
                stageData={funnel_overview.stage_5_opening_weekend}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
          </Grid>

          {/* Insights Panel */}
          {insights && (
            <Paper sx={{ p: 3, mb: 3, backgroundColor: `${brandColors.info}05`, border: `1px solid ${brandColors.info}40` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: brandColors.info }}>
                Key Insights
              </Typography>
              <Grid container spacing={2}>
                {insights.highest_converting_segment && (
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: `${brandColors.success}10`, border: `1px solid ${brandColors.success}40` }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: brandColors.success }}>
                          Best Converter
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insights.highest_converting_segment.segment}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insights.highest_converting_segment.demographic} • {formatConversionRate(insights.highest_converting_segment.conversion_rate)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {insights.highest_dropout_segment && (
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: `${brandColors.error}10`, border: `1px solid ${brandColors.error}40` }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: brandColors.error }}>
                          Highest Drop-off
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insights.highest_dropout_segment.segment}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insights.highest_dropout_segment.demographic} • {formatConversionRate(insights.highest_dropout_segment.conversion_rate)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {insights.lowest_converting_segment && (
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: `${brandColors.warning}10`, border: `1px solid ${brandColors.warning}40` }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: brandColors.warning }}>
                          Needs Attention
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insights.lowest_converting_segment.segment}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insights.lowest_converting_segment.demographic} • {formatConversionRate(insights.lowest_converting_segment.conversion_rate)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Demographic Conversion Comparison */}
          {demographic_breakdowns && demographic_breakdowns.length > 0 && (
            <DemographicConversionComparison
              demographicBreakdowns={demographic_breakdowns}
              colorSchemes={visualization_hints?.color_schemes}
            />
          )}
        </Box>
      )}

      {/* Demographic Flow Mode (Sankey) */}
      {viewMode === 'demographic' && (
        <Box>
          {sankeyNodes.length > 0 && sankeyLinks.length > 0 ? (
            <SankeyDiagramComponent
              nodes={sankeyNodes}
              links={sankeyLinks}
              title={`${selectedDemographic.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Flow Through Funnel`}
              height={600}
            />
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No demographic flow data available. Try lowering the minimum sample size or selecting a different demographic.
              </Typography>
            </Paper>
          )}

          {/* Stage Cards Below Sankey */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Stage Breakdown: {selectedDemographic.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={1}
                stageData={funnel_overview.stage_1_aware}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={2}
                stageData={funnel_overview.stage_2_interested}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={3}
                stageData={funnel_overview.stage_3_willing_to_pay}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={4}
                stageData={funnel_overview.stage_4_theater}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FunnelStageCard
                stageNumber={5}
                stageData={funnel_overview.stage_5_opening_weekend}
                demographicBreakdown={selectedDemo}
                showDemographics={true}
                totalResponses={film?.total_responses}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Drop-off Analysis Mode */}
      {viewMode === 'dropoff' && (
        <Box>
          <DropoffAnalysisPanel
            demographicBreakdowns={demographic_breakdowns}
            funnelOverview={funnel_overview}
          />
        </Box>
      )}
    </Box>
  );
};

export default ConversionFunnelAnalysis;

